import {
    Component,
    ContentChild,
    EventEmitter,
    forwardRef,
    Input, OnChanges,
    OnDestroy,
    OnInit,
    Output, SimpleChanges,
    TemplateRef, ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, } from '@angular/forms';
import {
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgSelectComponent,
    NgSelectModule
} from '@ng-select/ng-select';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil
} from 'rxjs';
import { ResponseSuccess } from '@core/models/response-success.model';
import { GlobalSelectCacheService } from '@core/services/global-select-cache.service';
import { NgTemplateOutlet } from '@angular/common';
import { GlobalSelectRegistryService } from '@core/services/global-select-registry.service';

@Component({
    selector: 'shared-global-select[type]',
    standalone: true,
    imports: [NgSelectModule, FormsModule, NgTemplateOutlet],
    template: `
        @if (error == null) {
            @if (preload) {
                <ng-select
                    [appendTo]="appendTo"
                    [items]="items"
                    [bindLabel]="bindLabel"
                    [bindValue]="bindValue ?? ''"
                    [placeholder]="placeholder"
                    [multiple]="multiple"
                    [clearable]="clearable"
                    [searchable]="searchable"
                    [closeOnSelect]="closeOnSelect && !multiple"
                    [loading]="loading || externalLoading"
                    [searchFn]="searchFn"
                    [disabled]="isDisabled"
                    [(ngModel)]="value"
                    (ngModelChange)="onValueChange($event)"
                    (open)="widenPanel()"
                    (blur)="onTouched()">
                    @if (labelTemplate) {
                        <ng-template ng-label-tmp let-item="item" let-clear="clear">
                            <ng-container *ngTemplateOutlet="labelTemplate; context: { item, clear }"/>
                        </ng-template>
                    }
                    @if (optionTemplate) {
                        <ng-template ng-option-tmp let-item="item" let-clear="clear" let-item$="item$" let-index="index">
                            <ng-container *ngTemplateOutlet="optionTemplate; context: { item, clear, item$, index }"/>
                        </ng-template>
                    }
                </ng-select>
            } @else {
                <ng-select
                    [appendTo]="appendTo"
                    [items]="items"
                    [bindLabel]="bindLabel"
                    [bindValue]="bindValue ?? ''"
                    [placeholder]="placeholder"
                    [multiple]="multiple"
                    [clearable]="clearable"
                    [searchable]="searchable"
                    [closeOnSelect]="closeOnSelect && !multiple"
                    [loading]="loading || externalLoading"
                    [typeahead]="search$"
                    [minTermLength]="3"
                    [searchFn]="searchFn"
                    [disabled]="isDisabled"
                    [(ngModel)]="value"
                    (ngModelChange)="onValueChange($event)"
                    (open)="widenPanel()"
                    (blur)="onTouched()">
                    @if (labelTemplate) {
                        <ng-template ng-label-tmp let-item="item" let-clear="clear">
                            <ng-container *ngTemplateOutlet="labelTemplate; context: { item, clear }"/>
                        </ng-template>
                    }
                    @if (optionTemplate) {
                        <ng-template ng-option-tmp let-item="item" let-clear="clear" let-item$="item$" let-index="index">
                            <ng-container *ngTemplateOutlet="optionTemplate; context: { item, clear, item$, index }"/>
                        </ng-template>
                    }
                </ng-select>
            }
        } @else {
            <div class="alert alert-danger">{{ error }}</div>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GlobalSelect),
            multi: true,
        },
    ],
})

export class GlobalSelect implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {

    @ViewChild(NgSelectComponent) ngSelect!: NgSelectComponent;

    @ContentChild(NgLabelTemplateDirective, {read: TemplateRef}) labelTemplate?: TemplateRef<any>;
    @ContentChild(NgOptionTemplateDirective, {read: TemplateRef}) optionTemplate?: TemplateRef<any>;

    @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemChange: EventEmitter<any> = new EventEmitter<any>();

    @Input() type!: string;
    @Input() bindValue: string|null = 'id';
    @Input() bindLabel: string = 'name';
    @Input() placeholder = '';
    @Input() multiple = false;
    @Input() clearable = true;
    @Input() searchable = true;
    @Input() params: any = {};
    @Input() preload: boolean = false;
    @Input() closeOnSelect: boolean = true;
    @Input() list: any[] = []; // Only when preload is false
    @Input() clearAfterSelect: boolean = false;
    @Input() appendTo: string = '';
    /** Copied onto the open dropdown, including when it is appended to body. */
    @Input() panelClass = '';
    @Input() externalLoading: boolean = false;
    @Input() searchFn: ((term: string, item: any) => boolean) | null = null;
    /** Prepends a blank choice so the value can be set back to null. */
    @Input() nullable = false;

    /**
     * Select types that represent a customer list. For these, options and the
     * selected value are displayed as "<id> - <name>" everywhere (see
     * decorateItems + the computed CUSTOMER_LABEL bindLabel).
     */
    private static readonly CUSTOMER_SELECT_TYPES = ['CuCustomerService@select'];
    private static readonly CUSTOMER_LABEL = '__customerLabel';

    get isCustomerSelect(): boolean {
        return GlobalSelect.CUSTOMER_SELECT_TYPES.includes(this.type)
            || /CustomerSelect$/i.test(this.type);
    }

    items: any[] = [];
    loading = false;
    value: any = null;
    isDisabled = false;

    error: any = null;

    readonly search$ = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private globalSelectCacheService: GlobalSelectCacheService,
        private globalSelectRegistryService: GlobalSelectRegistryService,
    ) {
    }

    widenPanel(): void {
        const panelClass = this.panelClass.trim();
        if (!panelClass) {
            return;
        }

        setTimeout(() => {
            document.querySelector('ng-dropdown-panel')?.classList.add(...panelClass.split(/\s+/));
        });
    }

    ngOnInit(): void {
        if (this.nullable && !this.searchFn) {
            this.searchFn = (term: string, item: any) => {
                if (item?.[this.bindValue ?? 'id'] == null) {
                    return true;
                }

                const label = String(item?.[this.bindLabel] ?? '').toLocaleLowerCase();
                return label.includes((term ?? '').toLocaleLowerCase());
            };
        }

        try {
            this.loadSelect();
        } catch (error) {
            this.error = error;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['params'] && !changes['params'].firstChange) {
            this.loadSelect();
        }

        if (changes['list'] && !this.preload) {
            this.mergeListItems();
        }
    }

    loadSelect() {
        this.error = null;

        // Customer dropdowns show "<id> - <name>" via a computed label field.
        if (this.isCustomerSelect) {
            this.bindLabel = GlobalSelect.CUSTOMER_LABEL;
        }

        const serviceName = this.type.split('@')[0];
        const methodName = this.type.split('@')[1];

        const service = this.globalSelectRegistryService.resolve(serviceName);

        if (service?.[methodName] === undefined) {
            throw new Error(`Service ${service.constructor.name} doesn't implement ${methodName} method`);
        }

        const result = service[methodName]({ ...this.params });
        const isArray = Array.isArray(result);

        if (isArray) this.preload = true;

        if (this.preload) {
            const cacheKey = this.globalSelectCacheService.buildKey(this.type, this.params);

            if (this.globalSelectCacheService.has(cacheKey)) {
                this.assignItems(this.globalSelectCacheService.get(cacheKey));
            } else {
                if (isArray) {
                    this.assignItems(result);
                } else {
                    const request$ = (service[methodName]({ ...this.params }) as Observable<ResponseSuccess>).pipe(
                        map(response => response.data ?? []),
                        catchError(() => of([])),
                    );

                    this.loading = true;

                    this.globalSelectCacheService.getOrSetPending(cacheKey, request$).pipe(
                        finalize(() => (this.loading = false)),
                        takeUntil(this.destroy$),
                    ).subscribe(items => this.assignItems(items));
                }
            }
        } else {
            this.assignItems(this.list);
            this.search$.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((q: string) => {
                    if (!q || q.length < 3) return of(this.items);

                    this.loading = true;

                    const params = {q, limit: 15, ...this.params};

                    return (service[methodName](params) as Observable<ResponseSuccess>).pipe(
                        map(response => response.data ?? []),
                        catchError(() => of([])),
                        finalize(() => (this.loading = false)),
                    );
                }),
                takeUntil(this.destroy$),
            ).subscribe(items => this.assignItems(items));
        }
    }

    private assignItems(items: any[] | null | undefined): void {
        const list = this.decorateItems(Array.isArray(items) ? items : []);
        this.items = this.prependNullableOption(list);
    }

    /** Keeps preselected rows (current customer, etc.) visible after a search. */
    private mergeListItems(): void {
        const incoming = this.decorateItems(Array.isArray(this.list) ? this.list : []);
        if (!incoming.length) {
            return;
        }

        const key = this.bindValue ?? 'id';
        const known = new Set(this.items.map((item) => item?.[key]));
        const missing = incoming.filter((item) => item?.[key] != null && !known.has(item[key]));
        if (!missing.length) {
            return;
        }

        this.items = this.prependNullableOption([...missing, ...this.items.filter((item) => item?.[key] != null)]);
    }

    private prependNullableOption(items: any[]): any[] {
        if (!this.nullable || this.multiple || !this.bindValue) {
            return items;
        }

        const key = this.bindValue;
        if (items.some((item) => item?.[key] == null)) {
            return items;
        }

        return [{ [key]: null, [this.bindLabel]: '---' }, ...items];
    }

    /**
     * For customer selects, add a computed "<id> - <name>" label used as
     * bindLabel so options and the selected value show the customer number.
     */
    private decorateItems(items: any[]): any[] {
        if (!this.isCustomerSelect || !Array.isArray(items)) {
            return items;
        }

        for (const item of items) {
            if (item && typeof item === 'object') {
                item[GlobalSelect.CUSTOMER_LABEL] = `${item.id} - ${item.name ?? ''}`;
            }
        }

        return items;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onValueChange(value: any): void {
        let item = value;
        if (this.bindValue) {
            const key = this.bindValue;
            item = this.items.find((item) => item?.[key] === value);
        }

        this.itemChange.emit(item);
        this.valueChange.emit(value);
        this.onChange(value);

        if (this.clearAfterSelect) {
            setTimeout(() => (this.value = null));
        }
    }

    onChange: (val: any) => void = () => {
    };
    onTouched: () => void = () => {
    };

    writeValue(val: any): void {
        this.value = val ?? null;
    }

    registerOnChange(fn: (val: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.isDisabled = disabled;
    }

    focus() {
        this.ngSelect?.focus();
        this.ngSelect?.open();
    }
}
